/**
 * A small personal list, kept with the account.
 *
 * Favourites and recently-played are the same shape of thing: a handful of
 * game ids that belong to one player, that nobody else may read, and that the
 * server has no opinion about. Both are written straight to Nakama's storage
 * from the client, which it allows for objects a user owns.
 *
 * ## Why not an RPC
 *
 * Every other per-user record in this app goes through one, because the server
 * has something to say about it - an entitlement is a purchase, a display name
 * is shown to other people. These two are decorations on your own screen. An
 * RPC would be a round trip through code whose only job was to write down what
 * the client already said.
 *
 * ## Why not localStorage
 *
 * They belong to the account, not the device. Starring a game on a phone and
 * finding it unstarred on a laptop reads as data loss, and so does a
 * recently-played list that forgets everything you played on the other screen.
 *
 * ## Written whole
 *
 * One record per list, replaced on every change. A record per game would turn
 * "what is on my list" into a query, and at this size there is no query worth
 * having.
 */

import type { Session } from '@heroiclabs/nakama-js';
import { client } from './client.js';

/** Everything personal lives here, one key per list. */
const COLLECTION = 'player';

/** Owner read, owner write: nobody else has any business with these. */
const OWNER_ONLY = { permissionRead: 1, permissionWrite: 1 };

/**
 * Read one list, or an empty one.
 *
 * A record that will not load reads as empty rather than as an error. These
 * are decorations on pages that work without them, and a banner over the
 * library would be louder than the thing that failed.
 */
export async function readGameIds(session: Session, key: string): Promise<string[]> {
  try {
    const objects = await client.readStorageObjects(session, {
      object_ids: [{ collection: COLLECTION, key, user_id: session.user_id ?? '' }],
    });

    const value = objects.objects?.[0]?.value as { games?: unknown } | undefined;
    if (!Array.isArray(value?.games)) return [];
    return value.games.filter((id): id is string => typeof id === 'string');
  } catch {
    return [];
  }
}

/**
 * What a player has played and won, as the server counted it.
 *
 * Read the same way the lists are, and for the same reason - it is owned by
 * the player, so there is nothing to decide about handing it over. It is
 * WRITTEN only by the server (`recordPlayerStats`), because a record you can
 * write yourself is not a record.
 */
export interface PlayerStats {
  readonly games: Readonly<Record<string, { played: number; won: number }>>;
  readonly totals: { played: number; won: number };
}

const NOTHING_PLAYED: PlayerStats = { games: {}, totals: { played: 0, won: 0 } };

export async function readStats(session: Session): Promise<PlayerStats> {
  try {
    const objects = await client.readStorageObjects(session, {
      object_ids: [{ collection: COLLECTION, key: 'stats', user_id: session.user_id ?? '' }],
    });

    const value = objects.objects?.[0]?.value as Partial<PlayerStats> | undefined;
    if (!value || typeof value.totals !== 'object') return NOTHING_PLAYED;
    return {
      games: value.games ?? {},
      totals: {
        played: Number(value.totals.played) || 0,
        won: Number(value.totals.won) || 0,
      },
    };
  } catch {
    // Nothing played yet reads the same as nothing stored, which is the
    // honest answer either way.
    return NOTHING_PLAYED;
  }
}

/** Replace one list. Rejects, so a caller can put the screen back. */
export function writeGameIds(
  session: Session,
  key: string,
  games: readonly string[],
): Promise<unknown> {
  return client.writeStorageObjects(session, [
    { collection: COLLECTION, key, value: { games: [...games] }, ...OWNER_ONLY },
  ]);
}
